import type { JourneyScreen, PendingAccess } from "@/journey/types";
import type { RuntimeMode, RuntimePhase } from "@/runtime/types";

export interface ScreenInput {
  webglAvailable: boolean;
  started: boolean;
  mode: RuntimeMode;
  phase: RuntimePhase;
  pendingAccess: PendingAccess;
  permissionDenied: boolean;
  noArSupport: boolean;
}

/**
 * Single source of truth for which wireframe frame is on screen. Kept pure so
 * the whole journey can be asserted in unit tests without a renderer.
 */
export function resolveScreen(input: ScreenInput): JourneyScreen {
  if (!input.webglAvailable) return "unsupported";
  if (!input.started) return "entry";
  if (input.phase === "error") return "error";
  if (input.permissionDenied) return "fallback-permission";
  if (input.pendingAccess !== null) return "permission";
  if (input.noArSupport) return "fallback-no-ar";
  if (input.phase === "loading-engine" || input.phase === "checking") return "preparing";
  if (input.phase === "paused") return "fallback-tracking";
  if (input.phase === "searching-surface") return "placement";
  if (input.phase === "placed") return "demo";
  // requesting-permission / recovering / stopped: the visitor is mid-transition
  // and the honest thing to show is the neutral "preparing" state.
  return "preparing";
}

const STATUS_MESSAGES: Readonly<Record<RuntimePhase, string>> = {
  idle: "Ready when you are.",
  "loading-engine": "Loading the experience…",
  checking: "Looking for AR support.",
  "requesting-permission": "Waiting for camera access…",
  "searching-surface": "Move your phone slowly to find a surface",
  placed: "Ready.",
  paused: "Move your phone slowly to relocate the surface.",
  recovering: "Reconnecting…",
  error: "Something went wrong.",
  stopped: "Session ended.",
};

export function statusMessage(phase: RuntimePhase): string {
  return STATUS_MESSAGES[phase];
}
